/** @jsx isolateComponent */

/* eslint-disable import/no-duplicates */
import type { ReactNode, MouseEvent as ReactMouseEvent } from 'react';
import { useState } from 'react';
import type { Interpolation } from '@emotion/css';

import SkipLink from '@zapier/universal-nav/dist/components/SkipLink';
import { ZapierLogo } from '@zapier/design-system-patterns';
import { useTrackingContext } from '@zapier/toolbox-tracking';
import { Animation, Colors, Link, useClickOutside } from '@zapier/design-system';
import {
  UniversalTopbarLinks,
  searchClosed,
  searchOpened,
  skipLinkHref,
} from '@zapier/universal-nav/dist/constants';
import {
  isolateComponent,
  labelStyles,
  withModifier,
} from '@zapier/common-utils';

import AccountActions from './AccountActions';
import SearchToggler from './SearchToggler';
import UniversalTopbarLink from './UniversalTopbarLink';
import SearchForm from './SearchForm';
import type { Props as SearchFormProps } from './SearchForm';

import type { UniversalNavLinkType } from '@zapier/universal-nav/dist/constants';
import {
  UNIVERSAL_TOPBAR_CONDENSED_HEIGHT,
  UNIVERSAL_TOPBAR_HEIGHT,
} from '@zapier/universal-nav/dist/constants';

export type Props = {
  /*
   * Indicates whether search fills the entire topbar or not.
   */
  isSearchFullWidth: boolean;
  /**
   * An array of links to be rendered inside the main navigation.
   */
  links: Array<UniversalNavLinkType>;
  /**
   * Zapier logo label for link
   */
  logoAriaLabel: string;
  /**
   * Zapier logo link
   */
  logoHref: string;
  /**
   * Optional handler for when anywhere on the top nav is clicked.
   */
  onClick?: (e: ReactMouseEvent) => void;
  /**
   * Optional handler for when somewhere outside of the nav is clicked.
   */
  onClickOutside?: (e: MouseEvent | TouchEvent) => void;
  /**
   * Zapier logo link callback
   */
  onLogoClick?: () => void;
  /**
   * Renders an account menu if provided. This overrides the default account actions menu (Log in, Sign up)
   */
  renderAccountMenu: (props: Props) => ReactNode;
  /**
   * Renders notifications component if provided
   */
  renderNotifications: (props: Props) => ReactNode;
  /**
   * Renders search component, or prevents it from rendering
   */
  renderSearchForm: (props: SearchFormProps) => ReactNode;
  /**
   * Render the sidebar component if provided
   */
  renderSidebar?: (props: Props) => ReactNode;
  /**
   * Currently selected link inside links
   */
  selectedHref: string;
  /**
   * Indicates whether the logo should render.
   */
  shouldRenderLogo: boolean;
  /**
   * Size of the topbar which adjusts its height, padding, etc.
   */
  size: 'small' | 'medium';
  /**
   * Anchor to the main content area of the page.
   * **The page must contain a node with the `id` in this prop!**
   */
  skipLinkHref: string;
};

type LinksProps = Props & { isSearching: boolean };

const MAIN_GRID_GAP = 30;

const Styles = labelStyles(__filename, {
  root: [
    {
      display: 'flex',
      justifyContent: 'center',
      backgroundColor: Colors.white,
      position: 'relative', // Needed for submenu dropdown
      borderBottom: `1px solid ${Colors.grey200}`,
    },
  ],
  header: (props: Props, { hasSidebar }: { hasSidebar: boolean }) => [
    {
      alignItems: 'stretch',
      display: 'grid',
      gridTemplateColumns: 'max-content auto max-content',
      width: '100%',
      backgroundColor: Colors.white,
    },
    !hasSidebar && withModifier('hasMaxWidth', {
      maxWidth: 1200
    }),
    withModifier(
      props.size,
      {
        small: {
          gap: 5,
          height: UNIVERSAL_TOPBAR_CONDENSED_HEIGHT,
          padding: '0 10px',
        },
        medium: {
          gap: MAIN_GRID_GAP,
          height: UNIVERSAL_TOPBAR_HEIGHT,
          padding: '0 20px',
          // Whenever there's a sidebar, and especially a sidebar toggle,
          // the toggle button itself takes up enough space to warrant
          // collapsing the padding on the left side.
          paddingLeft: hasSidebar ? 10 : 20,
        },
      }[props.size]
    ),
  ],
  group: (props: Props, { gap }: { gap?: number } = {}) => [
    {
      display: 'grid',
      gridAutoFlow: 'column',
      alignItems: 'center',
    },
    withModifier(props.size, {
      small: { gap: 15 },
      medium: { gap: 20 },
    }[props.size]),
    !!gap && withModifier(`gap-${gap}`, {
      gap
    }),
  ],
  mainNavigationContent: (props: Props) => [
    {
      display: 'flex',
      alignItems: 'stretch',
    },
    !props.isSearchFullWidth &&
      withModifier('offsetParent', {
        position: 'relative',
      }),
  ],
  searchForm: (props: Props, { isSearching }: { isSearching: boolean }) => [
    {
      position: 'absolute',
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
      zIndex: 1,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: Animation.transitionValue,
      opacity: 0,
      pointerEvents: 'none',
      transform: 'scaleX(0)',
    },
    isSearching &&
      withModifier('searching', {
        opacity: 1,
        pointerEvents: 'auto',
        transform: 'scaleX(1)',
      }),
    props.isSearchFullWidth &&
      withModifier('fullWidth', {
        // Ditch the `transform` effect on small screens.
        transform: 'none',
        backgroundColor: Colors.white,
        padding: '0 10px',
      }),
  ],
  searchToggler: ({ isSearching }: { isSearching: boolean }) => [
    {
      marginRight: -7,
      transition: Animation.transitionValue,
    },
    isSearching &&
      withModifier('hidden', {
        opacity: 0.2,
        pointerEvents: 'none',
      }),
  ],
  navLinks: {
    lineHeight: 1.5,
  },
  navLinksList: (props: LinksProps) => [
    {
      display: 'grid',
      gridGap: 30,
      gridTemplateColumns: `repeat(${props.links.length}, max-content)`,
    },
    props.isSearching &&
      withModifier('searching', {
        opacity: 0,
      }),
  ],
  logoLinksWrapper: {
    // This section has an element with position: absolute,
    // relative positioning here will define the bounds
    position: 'relative',
    // Optical alignment.
    marginTop: -5,
  },
  skipLink: {
    position: 'absolute',
    // Positions the SkipLink just below the logo
    top: '100%',
    whiteSpace: 'nowrap',
  },
});

const Links = (props: LinksProps) => {
  const links = props.links.map(link => (
    <li key={link.text}>
      <UniversalTopbarLink
        href={link.href}
        isSelected={props.selectedHref === link.href}
        submenu={link.submenu}
      >
        {link.text}
      </UniversalTopbarLink>
    </li>
  ));
  return (
    <nav css={Styles.navLinks as Interpolation}>
      <ul css={Styles.navLinksList(props) as Interpolation}>{links}</ul>
    </nav>
  );
};

/**
 * Renders universal navigation top bar with Zapier logo, search,
 * links and account actions.
 */
const UniversalTopbar = (props: Props) => {
  const { trackEvent } = useTrackingContext();
  const sidebar = props.renderSidebar && props.renderSidebar(props);
  const logoSize = {
    small: { width: 68, height: 31 },
    medium: { width: 80, height: 37 },
  }[props.size];
  // Used to tie search `input` to its search toggler `label`.
  const searchInputId = 'universal-nav-app-search';
  // Track whether the user is currently searching.
  const [isSearching, setIsSearching] = useState(false);
  const onCloseSearch = () => {
    if (isSearching) {
      trackEvent(searchClosed);
    }
    setIsSearching(false);
  };
  const searchForm = props.renderSearchForm({
    isSearching,
    searchInputId,
    onClose: onCloseSearch,
  });
  const [setClickOutsideNode] = useClickOutside({
    onClickOutside: (event: MouseEvent | TouchEvent) => {
      props.onClickOutside?.(event);
    }
  });

  return (
    // onClick here exists to support behaviour logic might needed by other components of the Nav.
    //  E.g; Closing the sidebar opened by Topbar if another element on Topbar is clicked.
    <div css={Styles.root as Interpolation} ref={node => setClickOutsideNode(node)}>
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions */}
      <header
        css={Styles.header(props, { hasSidebar: !!sidebar }) as Interpolation}
        data-testid="universalTopbar"
        onClick={props.onClick}
      >
        <div css={Styles.group(props, { gap: 5 }) as Interpolation}>
          {sidebar}
          {props.shouldRenderLogo && (
            <div css={Styles.logoLinksWrapper as Interpolation}>
              <Link
                ariaLabel={props.logoAriaLabel}
                href={props.logoHref}
                onClick={props.onLogoClick}
              >
                <ZapierLogo {...logoSize} />
              </Link>
              <div css={Styles.skipLink as Interpolation}>
                <SkipLink href={props.skipLinkHref} />
              </div>
            </div>
          )}
        </div>
        <div css={Styles.mainNavigationContent(props) as Interpolation}>
          {props.links.length > 0 && (
            <Links {...props} isSearching={isSearching} />
          )}
          {!!searchForm && (
            <div
              css={Styles.searchForm(props, { isSearching }) as Interpolation}
            >
              {searchForm}
            </div>
          )}
        </div>
        <div css={Styles.group(props) as Interpolation}>
          {!!searchForm && (
            /* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */
            <div
              css={Styles.searchToggler({ isSearching }) as Interpolation}
              data-testid="searchToggler"
              onClick={() => {
                setIsSearching(true);
              }}
            >
              <SearchToggler
                id={searchInputId}
                isSearching={false}
                label="Search apps…"
                onClick={() => {
                  trackEvent(searchOpened);
                }}
              />
            </div>
          )}
          {props.renderNotifications(props)}
          {props.renderAccountMenu(props)}
        </div>
      </header>
    </div>
  );
};

UniversalTopbar.defaultProps = {
  links: UniversalTopbarLinks,
  logoAriaLabel: 'Go to the Zapier Homepage',
  logoHref: '/',
  onLogoClick: () => {},
  renderAccountMenu: (props: Props) => (
    <AccountActions
      selectedHref={props.selectedHref}
      shouldRenderSignIn={props.size !== 'small'}
      shouldRenderSignUp={true}
      size={props.size}
    />
  ),
  renderNotifications: () => null,
  renderSearchForm: (props: SearchFormProps) => (
    <SearchForm
      isSearching={props.isSearching}
      onClose={props.onClose}
      searchInputId={props.searchInputId}
    />
  ),
  selectedHref: '',
  shouldRenderLogo: true,
  size: 'medium',
  skipLinkHref,
};

export default UniversalTopbar;
